import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Search, MapPin, X } from "lucide-react";

/**
 * Provider-agnostic geo autocomplete. Backend proxies GeoNames (Europe).
 * Emits normalized items { id, name, country_name, region, province, display }.
 */
export default function GeoSearchAutocomplete({ value, onChange, placeholder, testid = "geo-search", country, kinds = "admin" }) {
  const { t } = useI18n();
  const [q, setQ] = useState(value?.display || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQ(value?.display || ""); }, [value?.id]);

  const doSearch = async (query) => {
    if (!query || query.length < 2) { setItems([]); return; }
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/geo/search", { params: { q: query, country, kinds } });
      setItems(data || []);
      if ((data || []).length === 0) setError(t("geo_no_results_for_area"));
    } catch (e) {
      const raw = e.response?.data?.detail || t("geo_search_error");
      const friendly = /geo provider unavailable/i.test(raw) ? t("geo_temp_unavailable") : raw;
      setError(friendly);
      setItems([]);
    } finally { setLoading(false); }
  };

  const handleType = (val) => {
    setQ(val); setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const pick = (it) => {
    onChange && onChange(it);
    setQ(it.display); setItems([]); setOpen(false);
  };

  const clear = () => { onChange && onChange(null); setQ(""); setItems([]); setOpen(false); };

  return (
    <div className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid={`${testid}-input`}
          value={q}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => q.length >= 2 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder || t("country_or_province")}
          className="w-full h-9 pl-9 pr-8 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-emerald-500"
        />
        {value && (
          <button onClick={clear} data-testid={`${testid}-clear`} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X size={14} />
          </button>
        )}
      </div>
      {open && (loading || items.length > 0 || error) && (
        <div className="absolute left-0 right-0 top-11 z-[1200] bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-500">…</div>}
          {error && <div className="px-3 py-2 text-xs text-red-700 bg-red-50">{error}</div>}
          {items.map((it) => (
            <button
              key={it.id}
              data-testid={`${testid}-item-${it.id}`}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-0"
            >
              <div className="text-sm text-slate-900 font-semibold truncate">{it.name}</div>
              <div className="text-[11px] text-slate-500 truncate flex items-center gap-1"><MapPin size={10} /> {it.display}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
